export interface CreateLocaleConfigOptions {
  /** The locale the source content is authored in, e.g. "en-US". */
  sourceLocale: string;
  /**
   * Every non-source locale this app serves, mapped to its URL prefix,
   * including the pseudo locale (e.g. { "ja-JP": "ja", "pt-BR": "pt", "x-pseudo": "ps" }).
   * The source locale is deliberately omitted — it is unprefixed (canonical URL).
   */
  localeToPrefix: Record<string, string>;
  /** Which entry in localeToPrefix is the pseudo-translation locale. */
  pseudoLocale: string;
}

export interface LocaleConfig {
  sourceLocale: string;
  /** URL prefix for the source locale. The source locale carries no URL prefix, i.e. canonical URLs are unprefixed — this is only used to recognise/redirect an explicit "/en/..." URL. */
  DEFAULT_PREFIX: string;
  /** Every non-default prefix this app recognises, including the pseudo prefix. */
  KNOWN_PREFIXES: string[];
  PREFIX_TO_LOCALE: Record<string, string>;
  PSEUDO_PREFIX: string;
  /**
   * The pseudo-translation locale identifier (e.g. "x-pseudo"). Not a valid
   * BCP-47 tag, so it must never be handed to `Intl` — callers substitute
   * `sourceLocale` for formatting (see `createServerI18n`).
   */
  PSEUDO_LOCALE: string;
  /**
   * Resolves a URL prefix to its locale identifier.
   * The default prefix resolves to the source locale; unknown prefixes resolve to null.
   */
  resolveLocaleFromPrefix(prefix: string): string | null;
}

/**
 * Builds the locale/prefix lookup tables shared by routing, i18n instance
 * resolution, and href/image localisation. App-agnostic: pass in your own
 * source locale, locale-to-prefix map (including the pseudo locale), and
 * which locale is the pseudo one.
 */
export function createLocaleConfig(
  options: CreateLocaleConfigOptions,
): LocaleConfig {
  const { sourceLocale, localeToPrefix, pseudoLocale } = options;

  const DEFAULT_PREFIX = sourceLocale.split("-")[0];

  const PREFIX_TO_LOCALE: Record<string, string> = Object.fromEntries(
    Object.entries(localeToPrefix).map(([locale, prefix]) => [prefix, locale]),
  );

  const KNOWN_PREFIXES = Object.keys(PREFIX_TO_LOCALE);

  const PSEUDO_PREFIX = localeToPrefix[pseudoLocale];

  function resolveLocaleFromPrefix(prefix: string): string | null {
    if (prefix === DEFAULT_PREFIX) return sourceLocale;
    return PREFIX_TO_LOCALE[prefix] ?? null;
  }

  return {
    sourceLocale,
    DEFAULT_PREFIX,
    KNOWN_PREFIXES,
    PREFIX_TO_LOCALE,
    PSEUDO_PREFIX,
    PSEUDO_LOCALE: pseudoLocale,
    resolveLocaleFromPrefix,
  };
}
